import React, { useState, useEffect } from 'react';
import {
    AppBar, Toolbar, Button, Box, IconButton,
    Drawer, List, ListItem, ListItemText, useMediaQuery
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import PhoneIcon from '@mui/icons-material/Phone';
import { Link, useLocation } from 'react-router-dom';
import '@fontsource/playfair-display'

const navItems = [
    { label: 'HOME', path: '/' },
    { label: 'SERVICES', path: '/services' },
    { label: 'ABOUT US', path: '/about' },
    { label: 'CONTACT', path: '/contact' },
];

export default function Header() {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const isMobile = useMediaQuery('(max-width:1023px)');
    const location = useLocation();

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <>
            <AppBar
                position="fixed"
                elevation={scrolled ? 3 : 0}
                sx={{
                    backgroundColor: '#fff',
                    color: '#1a1a1a',
                    transition: 'box-shadow 0.3s ease',
                    borderBottom: '1px solid #f0f0f0',
                    zIndex: 1300,
                    height: 80,
                    justifyContent: 'center',
                }}
            >
                <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 2, lg: 4 }, py: 0.5, alignItems: 'center' }}>
                    {/* Logo */}
                    <Box component={Link} to="/" sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none', gap: 1 }}>
                        <Box
                            component="img"
                            src="/splash.png"
                            alt="Splash Beauty Lounge"
                            sx={{ height: { xs: 50, sm: 60 } }}
                            onError={(e) => {
                                e.target.style.display = 'none';
                            }}
                        />
                    </Box>

                    {/* Desktop Nav */}
                    {!isMobile && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: { md: 0.2, lg: 0.5 } }}>
                            {navItems.map((item) => {
                                const isActive = location.pathname === item.path;
                                return (
                                    <Button
                                        key={item.label}
                                        component={Link}
                                        to={item.path}
                                        sx={{
                                            color: isActive ? '#FF0099' : '#444',
                                            fontFamily: '"Playfair Display",Lato, sans-serif',
                                            fontWeight: 600,
                                            fontSize: { md: '0.72rem', lg: '0.78rem' },
                                            letterSpacing: '0.05em',
                                            px: { md: 1, lg: 1.5 },
                                            py: 1,
                                            borderRadius: 0,
                                            borderBottom: isActive ? '2px solid #FF0099' : '2px solid transparent',
                                            transition: 'all 0.2s',
                                            '&:hover': {
                                                color: '#FF0099',
                                                backgroundColor: 'transparent',
                                                borderBottom: '2px solid #FF0099',
                                            },
                                        }}
                                    >
                                        {item.label}
                                    </Button>
                                );
                            })}
                            <Button
                                
                                variant="contained"
                                startIcon={<PhoneIcon sx={{ fontSize: '0.9rem !important' }} />}
                                sx={{
                                    ml: { md: 1, lg: 2 },
                                    backgroundColor: '#FF0099',
                                    color: '#fff',
                                    borderRadius: '25px',
                                    px: { md: 2, lg: 2.5 },
                                    py: 0.8,
                                    fontSize: { md: '0.75rem', lg: '0.8rem' },
                                    fontWeight: 700,
                                    '&:hover': {
                                        backgroundColor: '#CC0077',
                                    },
                                    fontFamily: '"Playfair Display","Lato"',
                                    boxShadow: '0 4px 15px rgba(255,0,153,0.3)',
                                }}
                            >
                                Book Now
                            </Button>
                        </Box>
                    )}

{/* Mobile */}
                    {isMobile && (
                        <Box sx={{ display: 'flex', alignItems: 'start', gap: 0.5 }}>
                            <IconButton
                                component="a"
                                href="tel:8977626223"
                                variant="contained"
                                sx={{
                                    backgroundColor: '#FF0099',
                                    color: '#fff',
                                    borderRadius: 2,
                                    boxShadow: 3,
                                    p: 2,
                                    height: 32,
                                    width: 32,
                                    fontSize: '0.75rem',
                                    fontWeight: 700,
                                    mr: 1,
                                    '&:hover': { backgroundColor: '#CC0077' },
                                    fontFamily: '"Playfair Display","Lato"',
                                }}
                            >
                                <PhoneIcon />
                            </IconButton>
                            <IconButton onClick={() => setDrawerOpen(true)} sx={{
                                backgroundColor: '#FF0099',
                                color: '#fff',
                                borderRadius: 2,
                                boxShadow: 3,
                                p: 2,
                                height: 32,
                                width: 32,
                                fontSize: '0.75rem',
                                fontWeight: 700,
                                mr: 1,
                                '&:hover': { backgroundColor: '#CC0077' },
                                fontFamily: '"Playfair Display","Lato"',
                            }}>
                                <MenuIcon />
                            </IconButton>
                        </Box>
                    )}
                </Toolbar>
            </AppBar>

            {/* Mobile Drawer */}
            <Drawer anchor="right" open={drawerOpen} sx={{ zIndex: 1400 }} onClose={() => setDrawerOpen(false)}>
                <Box sx={{ width: 250, pt: 2 }}>
                    <List>
                        {navItems.map((item) => (
                            <ListItem
                                key={item.label}
                                component={Link}
                                to={item.path}
                                onClick={() => setDrawerOpen(false)}
                                sx={{
                                    color: location.pathname === item.path ? '#FF0099' : '#444',
                                    textDecoration: 'none',
                                    '&:hover': { backgroundColor: '#fff0f7' },
                                    fontFamily: '"Playfair Display","Lato"',
                                }}
                            >
                                <ListItemText
                                    primary={item.label}
                                    primaryTypographyProps={{
                                        fontFamily: '"Playfair Display","Lato"',
                                        fontWeight: 600,
                                        fontSize: '0.9rem',
                                        letterSpacing: '0.05em',
                                    }}
                                />
                            </ListItem>
                        ))}
                    </List>
                </Box>
            </Drawer>

            {/* Spacer for fixed header */}
            <Box sx={{ height: 80 }} />
        </>
    );
}
